// Validates build-time environment variables and exposes typed helpers.
import { z } from "zod";

const booleanFlag = z
  .enum(["true", "false"])
  .optional()
  .transform((value) => value === "true");

const publicUrlSchema = z.union([
  z.string().url(),
  z.literal(""),
  z.literal("/"),
]);

const apiEndpointSchema = z
  .union([
    z.string().url(),
    z.string().regex(/^\//, "Relative API base must start with '/'"),
  ])
  .default("/api");

const envSchema = z.object({
  NODE_ENV: z.enum(["development", "production", "test"]),
  HTTPS: booleanFlag,
  REACT_APP_API: apiEndpointSchema,
  PUBLIC_URL: publicUrlSchema,
  REACT_APP_MANAGEMENT_URL: z.string().url(),
  FF_OFFLINE_MODE: booleanFlag,
});

type EnvInput = z.input<typeof envSchema>;
type EnvKey = keyof EnvInput;

type EnvSource = Partial<Record<EnvKey, string | undefined>> & {
  MODE?: string;
  BASE_URL?: string;
};

const maybeProcessEnv: EnvSource =
  typeof process !== "undefined" && process.env
    ? (process.env as EnvSource)
    : {};

const maybeImportMetaEnv: EnvSource =
  typeof import.meta !== "undefined" && import.meta.env
    ? (import.meta.env as EnvSource)
    : {};

const envSources: readonly EnvSource[] = [maybeProcessEnv, maybeImportMetaEnv];

const readEnv = <Key extends EnvKey>(key: Key): EnvInput[Key] | undefined => {
  for (const source of envSources) {
    const value = source[key];
    if (typeof value !== "undefined") {
      return value as EnvInput[Key];
    }
  }

  return undefined;
};

const publicUrlValue =
  readEnv("PUBLIC_URL") ??
  maybeImportMetaEnv.PUBLIC_URL ??
  maybeImportMetaEnv.BASE_URL ??
  "";

const rawEnv: Partial<EnvInput> = {
  NODE_ENV:
    readEnv("NODE_ENV") ?? (maybeImportMetaEnv.MODE as EnvInput["NODE_ENV"]),
  HTTPS: readEnv("HTTPS"),
  PUBLIC_URL: publicUrlValue as EnvInput["PUBLIC_URL"],
  REACT_APP_MANAGEMENT_URL: readEnv("REACT_APP_MANAGEMENT_URL"),
  FF_OFFLINE_MODE: readEnv("FF_OFFLINE_MODE"),
};

const maybeApiBase = readEnv("REACT_APP_API");
if (typeof maybeApiBase !== "undefined") {
  rawEnv.REACT_APP_API = maybeApiBase;
}

const parsedEnv = envSchema.parse(rawEnv);

type Env = z.infer<typeof envSchema>;

export const env: Env = parsedEnv;

export const isProduction = env.NODE_ENV === "production";
const isDevelopment = env.NODE_ENV === "development";
const isOfflineMode = env.FF_OFFLINE_MODE;
